import {
  BUSINESS_BOOKING_ACTION_PERMISSIONS,
  BUSINESS_TABLE_ACTION_PERMISSIONS,
  DASHBOARD_REPORT_PERMISSIONS,
  hasPermissionRequirement,
  MEMBERSHIP_SETTING_ACTION_PERMISSIONS,
  ORDER_ACTION_PERMISSIONS,
  type PermissionRequirement,
  PROCUREMENT_ACTION_PERMISSIONS,
  SETTING_ACTION_PERMISSIONS,
  CHAIN_ACTION_PERMISSIONS as SHARED_CHAIN_ACTION_PERMISSIONS,
  DASHBOARD_ACTION_PERMISSIONS as SHARED_DASHBOARD_ACTION_PERMISSIONS,
  MEMBERSHIP_ACTION_PERMISSIONS as SHARED_MEMBERSHIP_ACTION_PERMISSIONS,
  SINGLE_STORE_MEMBER_ACTION_PERMISSIONS as SHARED_SINGLE_STORE_MEMBER_ACTION_PERMISSIONS,
  STOCK_ACTION_PERMISSIONS,
} from "@repo/teashop-utils"

export type { PermissionRequirement }

const toPermissionList = (requirement: PermissionRequirement): string[] => {
  return Array.isArray(requirement) ? [...requirement] : [requirement]
}

export {
  BUSINESS_BOOKING_ACTION_PERMISSIONS,
  BUSINESS_TABLE_ACTION_PERMISSIONS,
  DASHBOARD_REPORT_PERMISSIONS,
  hasPermissionRequirement,
  MEMBERSHIP_SETTING_ACTION_PERMISSIONS,
  ORDER_ACTION_PERMISSIONS,
  PROCUREMENT_ACTION_PERMISSIONS,
  SETTING_ACTION_PERMISSIONS,
  STOCK_ACTION_PERMISSIONS,
}

export const MEMBERSHIP_ACTION_PERMISSIONS = {
  ...SHARED_MEMBERSHIP_ACTION_PERMISSIONS,
  view: toPermissionList(SHARED_MEMBERSHIP_ACTION_PERMISSIONS.view),
} satisfies Record<string, PermissionRequirement>

export const DASHBOARD_ACTION_PERMISSIONS = {
  ...SHARED_DASHBOARD_ACTION_PERMISSIONS,
  exportReports: "dashboard_export_all",
} satisfies Record<string, PermissionRequirement>

export const SALES_FLOW_ACTION_PERMISSIONS = {
  exportReports: toPermissionList(SHARED_DASHBOARD_ACTION_PERMISSIONS.exportReports),
} satisfies Record<string, PermissionRequirement>

export const SINGLE_STORE_MEMBER_ACTION_PERMISSIONS = {
  ...SHARED_SINGLE_STORE_MEMBER_ACTION_PERMISSIONS,
  view: toPermissionList(SHARED_SINGLE_STORE_MEMBER_ACTION_PERMISSIONS.view),
} satisfies Record<string, PermissionRequirement>

export const CHAIN_ACTION_PERMISSIONS = {
  ...SHARED_CHAIN_ACTION_PERMISSIONS,
  view: toPermissionList(SHARED_CHAIN_ACTION_PERMISSIONS.view),
  viewPurchase: toPermissionList(SHARED_CHAIN_ACTION_PERMISSIONS.viewPurchase),
  viewOperate: toPermissionList(SHARED_CHAIN_ACTION_PERMISSIONS.viewOperate),
  viewMembers: toPermissionList(SHARED_CHAIN_ACTION_PERMISSIONS.viewMembers),
  exportMembers: toPermissionList(SHARED_CHAIN_ACTION_PERMISSIONS.exportMembers),
  manageMemberMarketing: toPermissionList(SHARED_CHAIN_ACTION_PERMISSIONS.manageMemberMarketing),
  manageMemberTags: toPermissionList(SHARED_CHAIN_ACTION_PERMISSIONS.manageMemberTags),
  manage: toPermissionList(SHARED_CHAIN_ACTION_PERMISSIONS.manage),
  manageMemberConfig: toPermissionList(SHARED_CHAIN_ACTION_PERMISSIONS.manageMemberConfig),
} satisfies Record<string, PermissionRequirement>

/**
 * HQ_ACTION_PERMISSIONS — SLICE-002 §F9 总部专属权限码空壳
 *
 * P0 业务切片(储值卡/返现/券/等级/品牌门面)落地时按需扩展。
 * 这里先占位,确保 router meta.permissions 有可引用的常量,
 * 后端权限元数据为权威定义。
 */
export const HQ_ACTION_PERMISSIONS = {
  /** 总部根菜单 */
  enter: ["hq_enter"] as string[],
  /** 储值规则配置 */
  manageStoredValueRules: ["hq_stored_value_rules_manage"] as string[],
  /** 返现规则配置(FU-E:已注册 hq_cashback_rules_manage 并挂超级管理员,移除 setting_shop_manage 兜底) */
  manageCashbackRules: ["hq_cashback_rules_manage"] as string[],
  /** 好友消费返现活动查看 */
  viewReferralCashbackActivities: ["hq_referral_cashback_view"] as string[],
  /** 好友消费返现活动创建、发布与启停 */
  manageReferralCashbackActivities: ["hq_referral_cashback_manage"] as string[],
  /** 券模板管理(FU-E:已注册 hq_coupon_templates_manage 并挂超级管理员,移除 setting_shop_manage 兜底) */
  manageCouponTemplates: ["hq_coupon_templates_manage"] as string[],
  /** 发券规则管理(FU-E:已注册 hq_coupon_rules_manage 并挂超级管理员,移除 setting_shop_manage 兜底) */
  manageCouponRules: ["hq_coupon_rules_manage"] as string[],
  /** 触达中心 */
  manageMessaging: ["hq_messaging_manage"] as string[],
  /** 储值清算台账查看 */
  viewSettlement: ["hq_settlement_view"] as string[],
  /** 储值清算台账管理 */
  manageSettlement: ["hq_settlement_manage"] as string[],
  /** 品牌门面 */
  manageBrandFacade: ["hq_brand_facade_manage"] as string[],
  /** 连锁门店管理入口 */
  viewStores: CHAIN_ACTION_PERMISSIONS.manage,
  /** 连锁区域管理入口 */
  manageRegions: CHAIN_ACTION_PERMISSIONS.manage,
  /** 连锁员工多门店绑定 */
  manageChainEmployeeBindings: ["chain_employee_bind"] as string[],
  /** 会员等级体系 */
  manageMemberLevels: ["hq_member_levels_manage"] as string[],
  /** 旧门店会员迁移到总部会员体系 */
  migrateMembers: ["hq_member_import"] as string[],
  /** 重复会员档案扫描与合并 */
  manageMemberDedupe: ["hq_member_dedupe_manage"] as string[],
  /** 总部会员名册 */
  viewMembers: ["hq_member_assets_view"] as string[],
  /** 总部选择来源门店并导入会员 */
  importMembers: ["hq_member_import"] as string[],
  /** 跨店会员资产看板 */
  viewMemberAssets: ["hq_member_assets_view"] as string[],
  /** 总部会员资料编辑 */
  updateMemberInfo: ["hq_member_info_update"] as string[],
  /** 总部会员余额手工调整 */
  adjustMemberBalance: ["hq_member_balance_adjust"] as string[],
  /** 品牌积分获取规则管理 */
  manageBrandPointRules: ["hq_brand_points_rules_manage"] as string[],
  /** 品牌积分抵现规则管理 */
  manageBrandPointDeductionRules: ["hq_brand_points_deduction_rules_manage"] as string[],
  /** 会员品牌积分人工调整；按钮级权限，不单独开放页面。 */
  adjustBrandPoints: ["hq_brand_points_adjust"] as string[],
  /** 门店旧积分比例维护与迁移试算；一期不包含正式迁移。 */
  previewBrandPointMigration: ["hq_brand_points_migration_preview"] as string[],
  /** 预订规则(P1) */
  manageReservationRules: ["hq_reservation_rules_manage"] as string[],
  /** 总部订单管理(FU-E:已注册 hq_orders_view / hq_orders_refund 并挂超级管理员,移除 setting_shop_manage 兜底) */
  manageOrders: ["hq_orders_view"] as string[],
  /** 总部订单退款；路由仍只要求查看权限，资金动作单独校验。 */
  refundOrders: ["hq_orders_refund"] as string[],
  /** 总部经营报表 */
  viewBusinessReports: ["hq_business_reports_view"] as string[],
  /** 总部经营报表敏感数据导出 */
  exportBusinessReports: ["hq_business_reports_export"] as string[],
  /** 经营目标配置；查看仍使用总部经营报表权限。 */
  manageOperationTargets: ["hq_operation_targets_manage"] as string[],
  /** 自定义报表模板管理；报表查看仍使用总部经营报表权限。 */
  manageCustomReports: ["hq_custom_reports_manage"] as string[],
  /** 自定义报表导出；与查看、模板管理分离。 */
  exportCustomReports: ["hq_custom_reports_export"] as string[],
} satisfies Record<string, PermissionRequirement>

export const HQ_ROOT_ACTION_PERMISSIONS = Array.from(new Set([
  ...toPermissionList(HQ_ACTION_PERMISSIONS.enter),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageStoredValueRules),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageCashbackRules),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.viewReferralCashbackActivities),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageReferralCashbackActivities),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageCouponTemplates),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageCouponRules),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageMessaging),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.viewSettlement),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageSettlement),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageBrandFacade),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.viewStores),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageRegions),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageChainEmployeeBindings),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageMemberLevels),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.migrateMembers),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageMemberDedupe),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.viewMembers),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.importMembers),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.viewMemberAssets),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.updateMemberInfo),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.adjustMemberBalance),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageBrandPointRules),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageBrandPointDeductionRules),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.adjustBrandPoints),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.previewBrandPointMigration),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageReservationRules),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageOrders),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.viewBusinessReports),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.exportBusinessReports),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageOperationTargets),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.manageCustomReports),
  ...toPermissionList(HQ_ACTION_PERMISSIONS.exportCustomReports),
]))
